// Vendored from @mapped/gtm-node lib/observability/logging.ts — keep in sync.
// This is the ONLY file permitted to call console.* (it is the stdout sink).
import { EventEmitter } from 'events'

export const LogEmitter = new EventEmitter()

export enum LogLevel {
  TRACE = 'trace',
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

type LogFormatter = (message: LogMessage) => string | void

export interface Logger {
  log: (message: string) => void
  trace: (message: string) => void
  debug: (message: string) => void
  info: (message: string) => void
  warn: (message: string) => void
  error: (message: string) => void
  getLevel: () => LogLevel
  with: () => LoggerContext
  isTraceEnabled: () => boolean
  isDebugEnabled: () => boolean
}

export interface LogMessage {
  level: LogLevel
  message: string
  ts?: string
  error?: {
    stack: string
    message: string
    toString: string
  }
  [key: string]: any
}

export interface LoggerContext {
  str: (key: string, value?: string) => LoggerContext
  num: (key: string, value?: number) => LoggerContext
  bool: (key: string, value?: boolean) => LoggerContext
  any: (key: string, value?: any, stringify?: boolean) => LoggerContext
  array: (key: string, value?: any[]) => LoggerContext
  error: (e: any) => LoggerContext
  logger: () => Logger
}

function resolveContextLevels(): string[] {
  const contextLevelsEnv = process.env.LOG_CONTEXT_FOR_LEVELS
  return contextLevelsEnv
    ? contextLevelsEnv.split(',')
    : ['trace', 'debug', 'info', 'warn', 'error']
}

const contextLevels = resolveContextLevels()

const LOG_CONTEXT_FOR_TRACE = contextLevels.includes('trace')
const LOG_CONTEXT_FOR_DEBUG = contextLevels.includes('debug')
const LOG_CONTEXT_FOR_INFO = contextLevels.includes('info')
const LOG_CONTEXT_FOR_WARN = contextLevels.includes('warn')
const LOG_CONTEXT_FOR_ERROR = contextLevels.includes('error')

// Fix: upstream used `new Boolean(process.env.LOG_TIMESTAMP).valueOf()`, which is
// truthy for the string 'false'. Compare explicitly instead.
const logTimestamp = process.env.LOG_TIMESTAMP === 'true'

export class MappedLogger implements Logger {
  protected _loglevel: LogLevel
  protected _ctx: Record<string, any>
  private formatter: LogFormatter

  private emptyMethod(..._args: any[]): void {}

  public log: (message: string) => void
  public trace: (message: string) => void
  public debug: (message: string) => void
  public info: (message: string) => void
  public warn: (message: string) => void
  public error: (message: string) => void

  constructor(level?: LogLevel, ctx?: Record<string, any>) {
    this.formatter = this.formatJson

    switch (process.env.LOG_FORMAT) {
      case 'json':
        this.formatter = this.formatJson
        break
      case 'simple':
        this.formatter = this.formatSimple
        break
    }

    this._loglevel = level ?? LogLevel.INFO
    this._ctx = ctx ?? {}

    this.log = this._log
    this.trace = this._trace
    this.debug = this._debug
    this.info = this._info
    this.warn = this._warn
    this.error = this._error

    switch (level) {
      case LogLevel.DEBUG:
        this.trace = this.emptyMethod
        break
      case LogLevel.INFO:
        this.trace = this.debug = this.emptyMethod
        break
      case LogLevel.WARN:
        this.trace = this.debug = this.info = this.emptyMethod
        break
      case LogLevel.ERROR:
        this.trace = this.debug = this.info = this.warn = this.emptyMethod
        break
    }
  }

  isTraceEnabled() {
    return this._loglevel === LogLevel.TRACE
  }

  isDebugEnabled() {
    return this._loglevel === LogLevel.DEBUG || this.isTraceEnabled()
  }

  getLevel() {
    return this._loglevel
  }

  private _log(message: string) {
    console.log({ message, ...this._ctx })
  }

  private emit(message: string, level: LogLevel, includeContext: boolean) {
    const logMessage: LogMessage = includeContext
      ? { message, ...this._ctx, level }
      : { message, level }
    LogEmitter.emit('log', logMessage)
    return logMessage
  }

  private _trace(message: string) {
    const logMessage = this.emit(message, LogLevel.TRACE, LOG_CONTEXT_FOR_TRACE)
    console.log(this.formatter(logMessage))
  }

  private _debug(message: string) {
    const logMessage = this.emit(message, LogLevel.DEBUG, LOG_CONTEXT_FOR_DEBUG)
    console.debug(this.formatter(logMessage))
  }

  private _info(message: string) {
    const logMessage = this.emit(message, LogLevel.INFO, LOG_CONTEXT_FOR_INFO)
    console.info(this.formatter(logMessage))
  }

  private _warn(message: string) {
    const logMessage = this.emit(message, LogLevel.WARN, LOG_CONTEXT_FOR_WARN)
    console.warn(this.formatter(logMessage))
  }

  private _error(message: string) {
    const logMessage = this.emit(message, LogLevel.ERROR, LOG_CONTEXT_FOR_ERROR)
    console.error(this.formatter(logMessage))
  }

  setCtx(key: string, value?: any) {
    this._ctx[key] = value
  }

  newLogger(level: LogLevel, ctx: Record<string, any>) {
    return new MappedLogger(level, JSON.parse(JSON.stringify(ctx)))
  }

  with() {
    const logger = this.newLogger(
      this._loglevel,
      JSON.parse(JSON.stringify(this._ctx))
    )
    return new MappedLogContext(logger)
  }

  formatJson(message: LogMessage) {
    if (logTimestamp) {
      message.ts = new Date().toISOString()
    }
    this.enrichJson(message)
    return safeStringify(message)
  }

  enrichJson(_message: LogMessage): void {}

  formatSimple(message: LogMessage) {
    const messageString = message.message
    const level = message.level

    delete (message as any).message
    delete (message as any).level

    const ts = logTimestamp ? ` [${new Date().toISOString()}] ` : ''
    const ctx =
      message && Object.keys(message).length > 0
        ? '\n' + JSON.stringify(message) + '\n'
        : ''

    switch (level) {
      case LogLevel.TRACE:
        return `\x1b[37m ${level.toUpperCase()} \x1b[0m ${ts} ${messageString}${
          LOG_CONTEXT_FOR_TRACE ? ctx : ''
        }`
      case LogLevel.DEBUG:
        return `\x1b[36m ${level.toUpperCase()} \x1b[0m ${ts} ${messageString}${
          LOG_CONTEXT_FOR_DEBUG ? ctx : ''
        }`
      case LogLevel.INFO:
        return `\x1b[32m ${level.toUpperCase()} \x1b[0m ${ts} ${messageString}${
          LOG_CONTEXT_FOR_INFO ? ctx : ''
        }`
      case LogLevel.WARN:
        return `\x1b[33m ${level.toUpperCase()} \x1b[0m ${ts} ${messageString}${
          LOG_CONTEXT_FOR_WARN ? ctx : ''
        }`
      case LogLevel.ERROR:
        return `\x1b[31m ${level.toUpperCase()} \x1b[0m ${ts} ${messageString}${
          LOG_CONTEXT_FOR_ERROR ? ctx : ''
        }`
      default:
        return `UNKNOWN: ${messageString}`
    }
  }
}

export class MappedLogContext implements LoggerContext {
  private _logger: MappedLogger

  constructor(logger: MappedLogger) {
    this._logger = logger
  }

  str(key: string, value?: string) {
    return this.any(key, value)
  }

  num(key: string, value?: number) {
    return this.any(key, value)
  }

  bool(key: string, value?: boolean) {
    return this.any(key, value)
  }

  array(key: string, value?: any[]) {
    return this.any(key, value)
  }

  error(e: any) {
    return this.any('error', {
      message: e?.message,
      stack: e?.stack,
      toString: e?.toString(),
    })
  }

  any(key: string, value?: any, stringify?: boolean) {
    this._logger.setCtx(key, stringify ? JSON.stringify(value) : value)
    return this
  }

  customerFacing() {
    return this.bool('customerFacing', true)
  }

  logger() {
    return this._logger
  }
}

export function safeStringify(value: any): string {
  const seen = new WeakSet()
  try {
    return JSON.stringify(value, (_k, v) => {
      if (typeof v === 'bigint') return v.toString()
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]'
        seen.add(v)
      }
      return v
    })
  } catch {
    return JSON.stringify({
      message: value?.message ?? 'unserializable log',
      level: value?.level,
      _stringifyError: true,
    })
  }
}

export function getLogger(): MappedLogger {
  const key = process.env.LOG_LEVEL?.toUpperCase() as
    | keyof typeof LogLevel
    | undefined
  const level = key && LogLevel[key] ? LogLevel[key] : LogLevel.INFO
  return new MappedLogger(level)
}
